import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowLeft, Users, CheckCircle, XCircle, CreditCard, X, Download, UserPlus, Search, Check, Trash2, AlertTriangle, Award } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventEnrollments = ({ eventId, eventTitle, onClose, fetchEnrollments: fetchEnrollmentsProp, userRole, certificatesStatus }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [paymentModal, setPaymentModal] = useState(null); // {enrollmentId, userName}
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [addUsersModal, setAddUsersModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [addingUsers, setAddingUsers] = useState(false);
  // State for removing participants
  const [selectedEnrollmentIds, setSelectedEnrollmentIds] = useState([]);
  const [removeConfirmModal, setRemoveConfirmModal] = useState(false);
  const [removingUsers, setRemovingUsers] = useState(false);
  // State for dance analysis editing
  const [danceAnalysisModal, setDanceAnalysisModal] = useState(null); // {enrollmentId, userName, requested, discipline}
  const [eventData, setEventData] = useState(null);
  // State for moving an enrollment to a different event
  const [moveEnrollmentModal, setMoveEnrollmentModal] = useState(null); // {enrollmentId, userName, currentEventId, currentEventTitle}
  const [allEventsForMove, setAllEventsForMove] = useState([]);
  const [moveTargetEventId, setMoveTargetEventId] = useState('');
  const [moveEventSearch, setMoveEventSearch] = useState('');
  const [moving, setMoving] = useState(false);
  // Certificates status from parent
  const [localCertificatesStatus, setLocalCertificatesStatus] = useState(null);

  useEffect(() => {
    if (eventId && isOpen) {
      fetchEnrollments();
      fetchCertificatesStatus();
    }
  }, [eventId, isOpen]);

  // If onClose is provided (admin mode), fetch immediately on mount
  useEffect(() => {
    if (eventId && onClose) {
      fetchEnrollments();
      fetchCertificatesStatus();
    }
  }, [eventId, onClose]);

  // Update local certificates status when prop changes
  useEffect(() => {
    if (certificatesStatus) {
      setLocalCertificatesStatus(certificatesStatus);
    }
  }, [certificatesStatus]);

  const fetchCertificatesStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/events/${eventId}/certificates-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocalCertificatesStatus(response.data);
    } catch (error) {
      console.log('Could not fetch certificates status');
    }
  };

  const fetchEventData = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      const ev = response.data.find(e => e.id === eventId);
      setEventData(ev || null);
    } catch (error) {
      console.log('Could not fetch event data');
    }
  };

  useEffect(() => { fetchEventData(); }, [eventId]);

  const updateDanceAnalysis = async (enrollmentId, requested, discipline) => {
    try {
      await axios.patch(`${API}/admin/enrollments/${enrollmentId}/dance-analysis`, {
        dance_analysis_requested: requested,
        dance_analysis_discipline: discipline
      });
      toast.success(requested ? 'Dance Analysis aggiunta' : 'Dance Analysis rimossa');
      setDanceAnalysisModal(null);
      fetchEnrollments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento Dance Analysis');
    }
  };

  const openMoveEnrollmentModal = async (enrollment) => {
    setMoveEnrollmentModal({
      enrollmentId: enrollment.id,
      userName: enrollment.user_name,
      currentEventId: eventId,
      currentEventTitle: eventTitle || (eventData?.title) || '',
    });
    setMoveTargetEventId('');
    setMoveEventSearch('');
    try {
      const resp = await axios.get(`${API}/events`);
      // Only events different from the current one
      setAllEventsForMove((resp.data || []).filter((e) => e.id !== eventId));
    } catch {
      setAllEventsForMove([]);
      toast.error('Impossibile caricare la lista eventi');
    }
  };

  const submitMoveEnrollment = async () => {
    if (!moveEnrollmentModal || !moveTargetEventId) return;
    setMoving(true);
    try {
      await axios.patch(`${API}/admin/enrollments/${moveEnrollmentModal.enrollmentId}/edit`, {
        event_id: moveTargetEventId,
      });
      toast.success('Iscrizione spostata sull\'evento di destinazione');
      setMoveEnrollmentModal(null);
      setMoveTargetEventId('');
      setMoveEventSearch('');
      // The enrollment now belongs to a different event, so it disappears from this list
      fetchEnrollments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nello spostamento dell\'iscrizione');
    } finally {
      setMoving(false);
    }
  };

  const fetchEnrollments = async () => {
    try {
      let response;
      if (fetchEnrollmentsProp) {
        // Use custom fetch function if provided (for teacher/formatore)
        const data = await fetchEnrollmentsProp(eventId);
        response = { data };
      } else {
        // Default to admin endpoint
        response = await axios.get(`${API}/admin/events/${eventId}/enrollments`);
      }
      setEnrollments(response.data);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
      toast.error('Errore nel caricamento degli iscritti');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setIsOpen(false);
    }
  };

  const updatePaymentStatus = async (enrollmentId, paid, userName) => {
    // If marking as paid, show payment method modal
    if (paid) {
      setPaymentModal({ enrollmentId, userName });
      return;
    }
    
    // If marking as unpaid, update directly
    try {
      const params = new URLSearchParams();
      params.append('paid', false);
      
      await axios.patch(`${API}/admin/enrollments/${enrollmentId}/payment?${params.toString()}`);
      toast.success('Pagamento annullato');
      fetchEnrollments(); // Refresh
    } catch (error) {
      console.error('Error updating payment:', error);
      const message = error.response?.data?.detail || 'Errore nell\'aggiornamento dello stato pagamento';
      toast.error(message);
    }
  };

  const confirmPayment = async () => {
    if (!selectedPaymentMethod) {
      toast.error('Seleziona un metodo di pagamento');
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append('paid', true);
      params.append('payment_method', selectedPaymentMethod);
      
      await axios.patch(`${API}/admin/enrollments/${paymentModal.enrollmentId}/payment?${params.toString()}`);
      toast.success(`Pagamento confermato - Metodo: ${selectedPaymentMethod}`);
      setPaymentModal(null);
      setSelectedPaymentMethod('');
      fetchEnrollments(); // Refresh
    } catch (error) {
      console.error('Error updating payment:', error);
      const message = error.response?.data?.detail || 'Errore nell\'aggiornamento dello stato pagamento';
      toast.error(message);
    }
  };

  // Fetch all users for manual enrollment
  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Filter out users already enrolled
      const enrolledUserIds = enrollments.map(e => e.user_id);
      const availableUsers = response.data.filter(u => 
        u.approved && !enrolledUserIds.includes(u.id)
      );
      setAllUsers(availableUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Errore nel caricamento degli utenti');
    }
  };

  // Open add users modal
  const openAddUsersModal = async () => {
    setAddUsersModal(true);
    setSelectedUserIds([]);
    setUserSearchQuery('');
    await fetchAllUsers();
  };

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Select/Deselect all filtered users
  const toggleSelectAll = () => {
    const filteredUsers = getFilteredUsers();
    const filteredIds = filteredUsers.map(u => u.id);
    const allSelected = filteredIds.every(id => selectedUserIds.includes(id));
    
    if (allSelected) {
      setSelectedUserIds(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedUserIds(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  // Filter users based on search query
  const getFilteredUsers = () => {
    if (!userSearchQuery.trim()) return allUsers;
    const query = userSearchQuery.toLowerCase();
    return allUsers.filter(user => 
      `${user.name} ${user.surname}`.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query)
    );
  };

  // Submit manual enrollments
  const submitManualEnrollments = async () => {
    if (selectedUserIds.length === 0) {
      toast.error('Seleziona almeno un utente');
      return;
    }

    setAddingUsers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/events/${eventId}/manual-enroll`,
        { user_ids: selectedUserIds },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      const { enrolled, skipped } = response.data;
      
      if (enrolled.length > 0) {
        toast.success(`${enrolled.length} partecipanti aggiunti con successo`);
      }
      
      if (skipped.length > 0) {
        const skippedNames = skipped.map(s => `${s.user_name || s.user_id}: ${s.reason}`).join(', ');
        toast.warning(`Alcuni utenti non sono stati aggiunti: ${skippedNames}`);
      }
      
      setAddUsersModal(false);
      setSelectedUserIds([]);
      fetchEnrollments(); // Refresh enrollments list
    } catch (error) {
      console.error('Error adding users:', error);
      const message = error.response?.data?.detail || 'Errore nell\'aggiunta dei partecipanti';
      toast.error(message);
    } finally {
      setAddingUsers(false);
    }
  };

  // Toggle enrollment selection for removal
  const toggleEnrollmentSelection = (enrollmentId) => {
    setSelectedEnrollmentIds(prev => 
      prev.includes(enrollmentId) 
        ? prev.filter(id => id !== enrollmentId)
        : [...prev, enrollmentId]
    );
  };

  // Select/Deselect all enrollments
  const toggleSelectAllEnrollments = () => {
    if (selectedEnrollmentIds.length === enrollments.length) {
      setSelectedEnrollmentIds([]);
    } else {
      setSelectedEnrollmentIds(enrollments.map(e => e.id));
    }
  };

  // Open remove confirmation modal
  const openRemoveConfirmModal = () => {
    if (selectedEnrollmentIds.length === 0) {
      toast.error('Seleziona almeno un partecipante da rimuovere');
      return;
    }
    setRemoveConfirmModal(true);
  };

  // Submit removal of enrollments
  const submitRemoveEnrollments = async () => {
    setRemovingUsers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API}/admin/events/${eventId}/enrollments`,
        { 
          headers: { 'Authorization': `Bearer ${token}` },
          data: { enrollment_ids: selectedEnrollmentIds }
        }
      );
      
      const { removed, not_found } = response.data;
      
      if (removed.length > 0) {
        toast.success(`${removed.length} partecipanti rimossi con successo`);
      }
      
      if (not_found.length > 0) {
        toast.warning(`${not_found.length} iscrizioni non trovate`);
      }
      
      setRemoveConfirmModal(false);
      setSelectedEnrollmentIds([]);
      fetchEnrollments(); // Refresh enrollments list
    } catch (error) {
      console.error('Error removing users:', error);
      const message = error.response?.data?.detail || 'Errore nella rimozione dei partecipanti';
      toast.error(message);
    } finally {
      setRemovingUsers(false);
    }
  };

  // Get selected enrollments info for confirmation
  const getSelectedEnrollmentsInfo = () => {
    return enrollments.filter(e => selectedEnrollmentIds.includes(e.id));
  };

  // Helper to check if user has certificate
  const userHasCertificate = (userId) => {
    if (!localCertificatesStatus?.enrollees) return false;
    const enrollee = localCertificatesStatus.enrollees.find(e => e.user_id === userId);
    return enrollee?.has_certificate || false;
  };

  const downloadExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/events/${eventId}/enrollments/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Iscritti_Evento.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('File Excel scaricato con successo');
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error('Errore nel download del file Excel');
    }
  };

  // If no onClose prop, render as button + modal
  if (!onClose) {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          className={userRole === 'formatore' ? 'text-purple-600 border-purple-200 hover:bg-purple-50' : ''}
          onClick={() => {
            setIsOpen(true);
            setLoading(true);
          }}
          data-testid={`view-enrollments-button-${eventId}`}
        >
          <Users className="w-4 h-4 mr-1" />
          {userRole === 'formatore' ? 'Vedi Iscritti' : 'Vedi Atleti'}
        </Button>

        {isOpen && (
          <EventEnrollmentsModal
            enrollments={enrollments}
            loading={loading}
            eventTitle={eventTitle}
            onClose={handleClose}
            userRole={userRole}
            updatePaymentStatus={updatePaymentStatus}
            paymentModal={paymentModal}
            selectedPaymentMethod={selectedPaymentMethod}
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            confirmPayment={confirmPayment}
            setPaymentModal={setPaymentModal}
            downloadExcel={downloadExcel}
            openAddUsersModal={openAddUsersModal}
            addUsersModal={addUsersModal}
            setAddUsersModal={setAddUsersModal}
            allUsers={allUsers}
            selectedUserIds={selectedUserIds}
            toggleUserSelection={toggleUserSelection}
            toggleSelectAll={toggleSelectAll}
            getFilteredUsers={getFilteredUsers}
            userSearchQuery={userSearchQuery}
            setUserSearchQuery={setUserSearchQuery}
            submitManualEnrollments={submitManualEnrollments}
            addingUsers={addingUsers}
            selectedEnrollmentIds={selectedEnrollmentIds}
            toggleEnrollmentSelection={toggleEnrollmentSelection}
            toggleSelectAllEnrollments={toggleSelectAllEnrollments}
            openRemoveConfirmModal={openRemoveConfirmModal}
            removeConfirmModal={removeConfirmModal}
            setRemoveConfirmModal={setRemoveConfirmModal}
            submitRemoveEnrollments={submitRemoveEnrollments}
            removingUsers={removingUsers}
            getSelectedEnrollmentsInfo={getSelectedEnrollmentsInfo}
            userHasCertificate={userHasCertificate}
            eventData={eventData}
            danceAnalysisModal={danceAnalysisModal}
            setDanceAnalysisModal={setDanceAnalysisModal}
            updateDanceAnalysis={updateDanceAnalysis}
            openMoveEnrollmentModal={openMoveEnrollmentModal}
            moveEnrollmentModal={moveEnrollmentModal}
            setMoveEnrollmentModal={setMoveEnrollmentModal}
            allEventsForMove={allEventsForMove}
            moveTargetEventId={moveTargetEventId}
            setMoveTargetEventId={setMoveTargetEventId}
            moveEventSearch={moveEventSearch}
            setMoveEventSearch={setMoveEventSearch}
            submitMoveEnrollment={submitMoveEnrollment}
            moving={moving}
          />
        )}
      </>
    );
  }

  // Otherwise render as modal directly
  return (
    <EventEnrollmentsModal
      enrollments={enrollments}
      loading={loading}
      eventTitle={eventTitle}
      onClose={handleClose}
      userRole={userRole}
      updatePaymentStatus={updatePaymentStatus}
      paymentModal={paymentModal}
      selectedPaymentMethod={selectedPaymentMethod}
      setSelectedPaymentMethod={setSelectedPaymentMethod}
      confirmPayment={confirmPayment}
      setPaymentModal={setPaymentModal}
      downloadExcel={downloadExcel}
      openAddUsersModal={openAddUsersModal}
      addUsersModal={addUsersModal}
      setAddUsersModal={setAddUsersModal}
      allUsers={allUsers}
      selectedUserIds={selectedUserIds}
      toggleUserSelection={toggleUserSelection}
      toggleSelectAll={toggleSelectAll}
      getFilteredUsers={getFilteredUsers}
      userSearchQuery={userSearchQuery}
      setUserSearchQuery={setUserSearchQuery}
      submitManualEnrollments={submitManualEnrollments}
      addingUsers={addingUsers}
      selectedEnrollmentIds={selectedEnrollmentIds}
      toggleEnrollmentSelection={toggleEnrollmentSelection}
      toggleSelectAllEnrollments={toggleSelectAllEnrollments}
      openRemoveConfirmModal={openRemoveConfirmModal}
      removeConfirmModal={removeConfirmModal}
      setRemoveConfirmModal={setRemoveConfirmModal}
      submitRemoveEnrollments={submitRemoveEnrollments}
      removingUsers={removingUsers}
      getSelectedEnrollmentsInfo={getSelectedEnrollmentsInfo}
      userHasCertificate={userHasCertificate}
      eventData={eventData}
      danceAnalysisModal={danceAnalysisModal}
      setDanceAnalysisModal={setDanceAnalysisModal}
      updateDanceAnalysis={updateDanceAnalysis}
      openMoveEnrollmentModal={openMoveEnrollmentModal}
      moveEnrollmentModal={moveEnrollmentModal}
      setMoveEnrollmentModal={setMoveEnrollmentModal}
      allEventsForMove={allEventsForMove}
      moveTargetEventId={moveTargetEventId}
      setMoveTargetEventId={setMoveTargetEventId}
      moveEventSearch={moveEventSearch}
      setMoveEventSearch={setMoveEventSearch}
      submitMoveEnrollment={submitMoveEnrollment}
      moving={moving}
    />
  );
};

// Modal component
const EventEnrollmentsModal = ({ 
  enrollments, 
  loading, 
  eventTitle, 
  onClose, 
  userRole, 
  updatePaymentStatus,
  paymentModal,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  confirmPayment,
  setPaymentModal,
  downloadExcel,
  openAddUsersModal,
  addUsersModal,
  setAddUsersModal,
  allUsers,
  selectedUserIds,
  toggleUserSelection,
  toggleSelectAll,
  getFilteredUsers,
  userSearchQuery,
  setUserSearchQuery,
  submitManualEnrollments,
  addingUsers,
  selectedEnrollmentIds,
  toggleEnrollmentSelection,
  toggleSelectAllEnrollments,
  openRemoveConfirmModal,
  removeConfirmModal,
  setRemoveConfirmModal,
  submitRemoveEnrollments,
  removingUsers,
  getSelectedEnrollmentsInfo,
  userHasCertificate,
  eventData,
  danceAnalysisModal,
  setDanceAnalysisModal,
  updateDanceAnalysis,
  openMoveEnrollmentModal,
  moveEnrollmentModal,
  setMoveEnrollmentModal,
  allEventsForMove,
  moveTargetEventId,
  setMoveTargetEventId,
  moveEventSearch,
  setMoveEventSearch,
  submitMoveEnrollment,
  moving
}) => {
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  const filteredUsers = getFilteredUsers ? getFilteredUsers() : [];
  const selectedEnrollmentsInfo = getSelectedEnrollmentsInfo ? getSelectedEnrollmentsInfo() : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={onClose}
                variant="ghost"
                size="sm"
                data-testid="close-enrollments-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Indietro
              </Button>
              <div>
                <h2 className="text-2xl font-bold text-gray-900" data-testid="enrollments-title">
                  Iscritti: {eventTitle}
                </h2>
                <p className="text-gray-600">
                  {enrollments.length} {userRole === 'formatore' ? 'insegnanti iscritti' : 'atleti iscritti'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Remove Selected button - only for admin */}
              {userRole !== 'teacher' && userRole !== 'formatore' && selectedEnrollmentIds && selectedEnrollmentIds.length > 0 && (
                <Button
                  onClick={openRemoveConfirmModal}
                  variant="outline"
                  size="sm"
                  className="bg-red-50 border-red-300 text-red-700 hover:bg-red-100"
                  data-testid="remove-selected-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Rimuovi ({selectedEnrollmentIds.length})
                </Button>
              )}
              
              {/* Add Users button - only for admin */}
              {userRole !== 'teacher' && userRole !== 'formatore' && openAddUsersModal && (
                <Button
                  onClick={openAddUsersModal}
                  variant="outline"
                  size="sm"
                  className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                  data-testid="add-users-button"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Aggiungi Partecipanti
                </Button>
              )}
              
              {/* Download Excel button - only for admin */}
              {userRole !== 'teacher' && userRole !== 'formatore' && downloadExcel && (
                <Button
                  onClick={downloadExcel}
                  variant="outline"
                  size="sm"
                  className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Scarica Excel
                </Button>
              )}
            </div>
          </div>
          
          {/* Selection controls - only for admin when there are enrollments */}
          {userRole !== 'teacher' && userRole !== 'formatore' && enrollments.length > 0 && toggleSelectAllEnrollments && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSelectAllEnrollments}
                data-testid="select-all-enrollments-button"
              >
                {selectedEnrollmentIds && selectedEnrollmentIds.length === enrollments.length
                  ? 'Deseleziona Tutti'
                  : 'Seleziona Tutti'}
              </Button>
              {selectedEnrollmentIds && selectedEnrollmentIds.length > 0 && (
                <Badge variant="secondary">
                  {selectedEnrollmentIds.length} selezionati
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {enrollments.length === 0 ? (
            <div className="text-center py-12" data-testid="no-enrollments">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">Nessun atleta iscritto</h3>
              <p className="text-gray-500">Non ci sono ancora iscrizioni per questo evento</p>
              {userRole !== 'teacher' && userRole !== 'formatore' && openAddUsersModal && (
                <Button
                  onClick={openAddUsersModal}
                  className="mt-4 bg-blue-600 hover:bg-blue-700"
                  data-testid="add-users-empty-button"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Aggiungi Partecipanti
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4" data-testid="enrollments-list">
              {enrollments.map((enrollment) => (
                <Card 
                  key={enrollment.id} 
                  className={`transition-colors ${
                    selectedEnrollmentIds && selectedEnrollmentIds.includes(enrollment.id) 
                      ? 'bg-red-50 border-red-200' 
                      : 'hover:bg-gray-50'
                  }`}
                  data-testid={`enrollment-${enrollment.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {/* Checkbox for selection - only for admin */}
                        {userRole !== 'teacher' && userRole !== 'formatore' && toggleEnrollmentSelection && (
                          <div 
                            onClick={() => toggleEnrollmentSelection(enrollment.id)}
                            className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ${
                              selectedEnrollmentIds && selectedEnrollmentIds.includes(enrollment.id)
                                ? 'bg-red-600 border-red-600'
                                : 'border-gray-300 hover:border-red-400'
                            }`}
                            data-testid={`enrollment-checkbox-${enrollment.id}`}
                          >
                            {selectedEnrollmentIds && selectedEnrollmentIds.includes(enrollment.id) && (
                              <Check className="w-4 h-4 text-white" />
                            )}
                          </div>
                        )}
                        
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="w-6 h-6 text-blue-600" />
                        </div>
                        
                        <div>
                          <div className="flex items-center">
                            <h3 className="font-semibold text-lg" data-testid={`enrollment-name-${enrollment.id}`}>
                              {enrollment.user_name}
                            </h3>
                            {userHasCertificate && userHasCertificate(enrollment.user_id) && (
                              <span 
                                className="ml-2 text-amber-500" 
                                title="Attestato generato"
                                data-testid={`certificate-icon-${enrollment.id}`}
                              >
                                <Award className="w-5 h-5" />
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600" data-testid={`enrollment-date-${enrollment.id}`}>
                            Iscritto il: {new Date(enrollment.enrolled_at).toLocaleDateString('it-IT', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          {enrollment.selected_dates && enrollment.selected_dates.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1" data-testid={`enrollment-selected-dates-${enrollment.id}`}>
                              <span className="text-xs text-blue-700 font-semibold mr-1">Date:</span>
                              {enrollment.selected_dates.map(d => (
                                <span key={d} className="inline-block px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700 font-medium">
                                  {new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        {/* Payment Status - only for admin */}
                        {userRole !== 'teacher' && userRole !== 'formatore' && (
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center space-x-2">
                              <CreditCard className={`w-4 h-4 ${enrollment.paid ? 'text-green-600' : 'text-red-600'}`} />
                              <Label htmlFor={`payment-${enrollment.id}`} className="text-sm font-medium">
                                Pagamento:
                              </Label>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Badge 
                                variant={enrollment.paid ? "default" : "destructive"}
                                className={enrollment.paid ? "bg-green-500" : "bg-red-500"}
                                data-testid={`payment-status-${enrollment.id}`}
                              >
                                {enrollment.paid ? (
                                  <>
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Pagato
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="w-3 h-3 mr-1" />
                                    Non Pagato
                                  </>
                                )}
                              </Badge>
                              
                              <Switch
                                id={`payment-${enrollment.id}`}
                                checked={enrollment.paid}
                                onCheckedChange={(checked) => updatePaymentStatus(enrollment.id, checked, enrollment.user_name)}
                                data-testid={`payment-switch-${enrollment.id}`}
                              />
                            </div>
                            {enrollment.paid && enrollment.payment_method && (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                                {enrollment.payment_method}
                              </Badge>
                            )}
                          </div>
                        )}
                        {/* For teacher/formatore, just show payment status badge */}
                        {(userRole === 'teacher' || userRole === 'formatore') && (
                          <Badge 
                            variant={enrollment.paid ? "default" : "destructive"}
                            className={enrollment.paid ? "bg-green-500" : "bg-red-500"}
                            data-testid={`payment-status-${enrollment.id}`}
                          >
                            {enrollment.paid ? (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Pagato
                              </>
                            ) : (
                              <>
                                <XCircle className="w-3 h-3 mr-1" />
                                Non Pagato
                              </>
                            )}
                          </Badge>
                        )}

                        {/* Dance Analysis Status */}
                        {eventData && eventData.dance_analysis && (
                          <div className="flex items-center gap-2 mt-2" data-testid={`da-status-${enrollment.id}`}>
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                              enrollment.dance_analysis_requested
                                ? 'bg-purple-100 text-purple-800 border border-purple-200'
                                : 'bg-gray-100 text-gray-500 border border-gray-200'
                            }`}>
                              {enrollment.dance_analysis_requested ? (
                                <>DA: SI</>
                              ) : (
                                <>DA: NO</>
                              )}
                            </span>
                            {userRole === 'admin' && (
                              <>
                                <button
                                  onClick={() => setDanceAnalysisModal({
                                    enrollmentId: enrollment.id,
                                    userName: enrollment.user_name,
                                    requested: enrollment.dance_analysis_requested || false,
                                    discipline: enrollment.dance_analysis_discipline || ''
                                  })}
                                  className="text-xs text-purple-600 hover:text-purple-800 underline"
                                  data-testid={`edit-da-${enrollment.id}`}
                                >
                                  Modifica DA
                                </button>
                                <button
                                  onClick={() => openMoveEnrollmentModal && openMoveEnrollmentModal(enrollment)}
                                  className="text-xs text-blue-600 hover:text-blue-800 underline ml-3"
                                  data-testid={`move-enrollment-${enrollment.id}`}
                                  title="Sposta su un altro evento"
                                >
                                  Sposta Evento
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        
        {/* Payment Method Modal */}
        {paymentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Conferma Pagamento</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPaymentModal(null);
                    setSelectedPaymentMethod('');
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <p className="text-gray-600 mb-4">
                Conferma il pagamento per <strong>{paymentModal.userName}</strong>
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="payment-method" className="mb-2 block">
                    Metodo di Pagamento <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={selectedPaymentMethod}
                    onValueChange={setSelectedPaymentMethod}
                  >
                    <SelectTrigger id="payment-method">
                      <SelectValue placeholder="Seleziona metodo..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bonifico">Bonifico</SelectItem>
                      <SelectItem value="On-Line">On-Line</SelectItem>
                      <SelectItem value="Contanti">Contanti</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    onClick={confirmPayment}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={!selectedPaymentMethod}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Conferma Pagamento
                  </Button>
                  <Button
                    onClick={() => {
                      setPaymentModal(null);
                      setSelectedPaymentMethod('');
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Annulla
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Add Users Modal */}
        {addUsersModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
              <div className="flex justify-between items-center p-6 border-b">
                <div>
                  <h3 className="text-xl font-semibold">Aggiungi Partecipanti</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Seleziona gli utenti da iscrivere all'evento
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setAddUsersModal(false);
                    setUserSearchQuery('');
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Search and Select All */}
              <div className="p-4 border-b space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Cerca per nome, cognome o email..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="user-search-input"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleSelectAll}
                    data-testid="select-all-button"
                  >
                    {filteredUsers.length > 0 && filteredUsers.every(u => selectedUserIds.includes(u.id))
                      ? 'Deseleziona Tutti'
                      : 'Seleziona Tutti'}
                  </Button>
                  <Badge variant="secondary">
                    {selectedUserIds.length} selezionati
                  </Badge>
                </div>
              </div>
              
              {/* Users List */}
              <div className="flex-1 overflow-y-auto p-4">
                {filteredUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Nessun utente disponibile</p>
                    <p className="text-sm">Tutti gli utenti sono già iscritti o non corrispondono alla ricerca</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => toggleUserSelection(user.id)}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedUserIds.includes(user.id)
                            ? 'bg-blue-50 border-blue-300'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                        data-testid={`user-option-${user.id}`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            selectedUserIds.includes(user.id) ? 'bg-blue-200' : 'bg-gray-200'
                          }`}>
                            <Users className={`w-5 h-5 ${
                              selectedUserIds.includes(user.id) ? 'text-blue-700' : 'text-gray-500'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium">{user.name} {user.surname}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                            <div className="flex gap-1 mt-1">
                              {user.roles?.map((role, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {role}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          selectedUserIds.includes(user.id)
                            ? 'bg-blue-600 border-blue-600'
                            : 'border-gray-300'
                        }`}>
                          {selectedUserIds.includes(user.id) && (
                            <Check className="w-4 h-4 text-white" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="p-4 border-t flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setAddUsersModal(false);
                    setUserSearchQuery('');
                  }}
                >
                  Annulla
                </Button>
                <Button
                  onClick={submitManualEnrollments}
                  disabled={selectedUserIds.length === 0 || addingUsers}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="confirm-add-users-button"
                >
                  {addingUsers ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Aggiunta in corso...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Aggiungi {selectedUserIds.length} Partecipanti
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Remove Confirmation Modal */}
        {removeConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Conferma Rimozione</h3>
                    <p className="text-sm text-gray-500">Questa azione non può essere annullata</p>
                  </div>
                </div>
                
                <p className="text-gray-700 mb-4">
                  Stai per rimuovere <strong>{selectedEnrollmentsInfo.length}</strong> partecipant{selectedEnrollmentsInfo.length === 1 ? 'e' : 'i'} dall'evento:
                </p>
                
                <div className="bg-gray-50 rounded-lg p-3 mb-4 max-h-40 overflow-y-auto">
                  {selectedEnrollmentsInfo.map((enrollment, idx) => (
                    <div key={enrollment.id} className="flex items-center space-x-2 py-1">
                      <Trash2 className="w-4 h-4 text-red-500" />
                      <span className="text-sm">{enrollment.user_name}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex space-x-3">
                  <Button
                    onClick={() => setRemoveConfirmModal(false)}
                    variant="outline"
                    className="flex-1"
                    disabled={removingUsers}
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={submitRemoveEnrollments}
                    disabled={removingUsers}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    data-testid="confirm-remove-button"
                  >
                    {removingUsers ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Rimozione...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Rimuovi
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dance Analysis Edit Modal */}
        {danceAnalysisModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Modifica Dance Analysis</h3>
                <Button variant="ghost" size="sm" onClick={() => setDanceAnalysisModal(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-600 mb-4">Atleta: <strong>{danceAnalysisModal.userName}</strong></p>
              <div className="space-y-4">
                <label className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  danceAnalysisModal.requested ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                }`}>
                  <input
                    type="checkbox"
                    checked={danceAnalysisModal.requested}
                    onChange={(e) => setDanceAnalysisModal({...danceAnalysisModal, requested: e.target.checked, discipline: e.target.checked ? danceAnalysisModal.discipline : ''})}
                    className="w-4 h-4 text-purple-600 rounded mr-3"
                    data-testid="modal-da-checkbox"
                  />
                  <span className="text-sm font-medium">Partecipa alla Dance Analysis</span>
                </label>
                {danceAnalysisModal.requested && (
                  <div>
                    <label className="block text-sm font-semibold mb-1">Disciplina Dance Analysis *</label>
                    <input
                      type="text"
                      value={danceAnalysisModal.discipline}
                      onChange={(e) => setDanceAnalysisModal({...danceAnalysisModal, discipline: e.target.value})}
                      placeholder="Es: Latin Style, Hip Hop..."
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                      data-testid="modal-da-discipline"
                    />
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setDanceAnalysisModal(null)}>Annulla</Button>
                <Button
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => updateDanceAnalysis(danceAnalysisModal.enrollmentId, danceAnalysisModal.requested, danceAnalysisModal.discipline)}
                  data-testid="modal-da-save"
                >
                  Salva
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Move Enrollment Modal */}
        {moveEnrollmentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Sposta Iscrizione su altro Evento</h3>
                <Button variant="ghost" size="sm" onClick={() => setMoveEnrollmentModal(null)} disabled={moving}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                Atleta: <strong data-testid="move-modal-user">{moveEnrollmentModal.userName}</strong>
              </p>
              <p className="text-xs text-gray-500 mb-4">
                Evento attuale: <span className="italic">{moveEnrollmentModal.currentEventTitle || moveEnrollmentModal.currentEventId}</span>
              </p>
              <div className="space-y-3">
                <Label htmlFor="move-event-search">Cerca evento di destinazione</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="move-event-search"
                    type="text"
                    value={moveEventSearch}
                    onChange={(e) => setMoveEventSearch(e.target.value)}
                    placeholder="Filtra per titolo o location..."
                    className="pl-9"
                    data-testid="move-event-search-input"
                    disabled={moving}
                  />
                </div>
                <div className="border rounded-lg max-h-60 overflow-y-auto" data-testid="move-event-list">
                  {(() => {
                    const q = (moveEventSearch || '').trim().toLowerCase();
                    const filtered = (allEventsForMove || []).filter((ev) => {
                      if (!q) return true;
                      return (ev.title || '').toLowerCase().includes(q)
                        || (ev.location || '').toLowerCase().includes(q);
                    });
                    if (filtered.length === 0) {
                      return <p className="p-4 text-sm text-gray-500 text-center">Nessun evento trovato</p>;
                    }
                    return filtered.map((ev) => (
                      <label
                        key={ev.id}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-blue-50 ${moveTargetEventId === ev.id ? 'bg-blue-100' : ''}`}
                        data-testid={`move-event-option-${ev.id}`}
                      >
                        <input
                          type="radio"
                          name="moveTargetEvent"
                          value={ev.id}
                          checked={moveTargetEventId === ev.id}
                          onChange={() => setMoveTargetEventId(ev.id)}
                          disabled={moving}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{ev.title}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {ev.date_tbd ? 'Data da Definire' : (ev.start_date || 'Senza data')}
                            {ev.location ? ` · ${ev.location}` : ''}
                          </p>
                        </div>
                      </label>
                    ));
                  })()}
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setMoveEnrollmentModal(null)} disabled={moving}>
                  Annulla
                </Button>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={submitMoveEnrollment}
                  disabled={!moveTargetEventId || moving}
                  data-testid="move-enrollment-confirm-btn"
                >
                  {moving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Spostamento...
                    </>
                  ) : (
                    <>Sposta su evento selezionato</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventEnrollments;